import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Avatar,
  Divider,
  Stack,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TableSortLabel,
  Switch,
  FormControlLabel,
  Popover,
  Skeleton,
  Badge,
  Fade,
  ButtonBase,
  Collapse
} from '@mui/material';
import {
  SportsBaseball,
  Schedule,
  Stadium,
  Close,
  FilterList,
  Sort,
  Timeline,
  Groups,
  Assessment,
  Home,
  Flight,
  Refresh,
  Person,
  OpenInNew,
  ArrowUpward,
  ArrowDownward,
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { getTeamLogoUrl as getSharedTeamLogoUrl } from '../utils/teamLogos';
import WinProbabilityChart from './WinProbabilityChart';
import AtBatExplorer from './AtBatExplorer';
import { matchupsApi } from '../services/apiService';

// ── Utilities ──
const getTeamLogoUrl = (teamCode) => getSharedTeamLogoUrl(teamCode, 500);

const getPlayerHeadshotUrl = (playerId) =>
  `https://img.mlbstatic.com/mlb-photos/image/upload/d_people:generic:headshot:67:current.png/w_213,q_auto:best/v1/people/${playerId}/headshot/67/current`;

const formatGameTime = (dateTime) => {
  if (!dateTime) return '';
  try {
    return new Date(dateTime).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
  } catch {
    return dateTime;
  }
};

const formatGameDate = (dateTime) => {
  if (!dateTime) return '';
  try {
    return new Date(dateTime).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch {
    return dateTime;
  }
};

// Extract short team name from full name: "Houston Astros" → "Astros"
const getShortName = (fullName) => {
  if (!fullName) return '';
  const parts = fullName.split(' ');
  return parts.length > 1 ? parts[parts.length - 1] : fullName;
};

// ──────────────────────────────────────────────────────────────────────────────
// BOXSCORE COMPONENT
// ──────────────────────────────────────────────────────────────────────────────
const Boxscore = ({ game, open, onClose, embedded = false, onNavigateToFull, onLiveDataUpdate }) => {
  const theme = useTheme();
  const isMobileView = typeof window !== 'undefined' && window.innerWidth < 600;
  const [currentTab, setCurrentTab] = useState(0);
  const [detailedStats, setDetailedStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [playByPlaySort, setPlayByPlaySort] = useState('recent');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [newPlaysCount, setNewPlaysCount] = useState(0);
  const [expandedPlay, setExpandedPlay] = useState(null);

  // Matchup state
  const [matchupData, setMatchupData] = useState(null);
  const [matchupLoading, setMatchupLoading] = useState(false);

  // Ref for stable auto-refresh callback (avoids interval churn)
  const detailedStatsRef = useRef(null);

  // Stash onLiveDataUpdate in a ref so the parent passing an inline arrow
  // (LiveScoreboard does) doesn't churn fetchDetailedStats's identity, which
  // would otherwise refire the open-effect on every render → skeleton shutter.
  const onLiveDataUpdateRef = useRef(onLiveDataUpdate);
  useEffect(() => { onLiveDataUpdateRef.current = onLiveDataUpdate; }, [onLiveDataUpdate]);

  // Popup state
  const [inningPopup, setInningPopup] = useState(null);  // { inning: number }
  const [playerPopup, setPlayerPopup] = useState(null);   // { player, anchorEl, type: 'batter'|'pitcher' }
  const [matchupExplorer, setMatchupExplorer] = useState(null); // { batterTeam, batterName, pitcherTeam, pitcherName, perspective }

  // ── Fetch detailed stats ──
  const fetchDetailedStats = useCallback(async (isRefresh = false) => {
    if (!game?.id) return;
    try {
      if (!isRefresh) setLoadingStats(true);
      const response = await fetch(`/api/v2/mlb-live/boxscore/${game.id}?t=${Date.now()}`);
      const data = await response.json();

      // Compare against ref (not state) so callback stays stable
      if (isRefresh && detailedStatsRef.current?.playByPlay && data.playByPlay) {
        const diff = data.playByPlay.length - detailedStatsRef.current.playByPlay.length;
        if (diff > 0) {
          setNewPlaysCount(diff);
          setTimeout(() => setNewPlaysCount(0), 3000);
        }
      }
      detailedStatsRef.current = data;
      setDetailedStats(data);
      // Notify parent (e.g. LiveScoreboard) so it can sync GameCard linescore
      const cb = onLiveDataUpdateRef.current;
      if (cb && game?.id) {
        try { cb(game.id, data); } catch (e) { /* no-op */ }
      }
      return data;
    } catch (error) {
      console.error('Failed to fetch detailed stats:', error);
    } finally {
      if (!isRefresh) setLoadingStats(false);
    }
  }, [game?.id]); // stable — only depends on game.id; parent callback read via ref

  // ── Game status helper ──
  const getGameStatus = () => {
    if (!game) return { status: 'Scheduled', color: theme.palette.primary.main };
    // Prefer fresh detailedStats over stale game prop for live/final detection
    const dsStatus = (detailedStats?.status || '').toLowerCase();
    const dsIsFinal = dsStatus.includes('final') || dsStatus.includes('completed') || dsStatus.includes('game over');
    const dsIsLive = detailedStats?.isLive === true;
    if (game.isFinal || dsIsFinal) return { status: 'Final', color: theme.palette.text.secondary };
    if (game.isLive || dsIsLive) {
      const ls = detailedStats?.linescore || game?.mlbGameData?.linescore;
      const inning = ls?.currentInning ? `${ls.currentInningOrdinal || ls.currentInning}` : '';
      const state = ls?.inningState ? ` ${ls.inningState}` : '';
      return { status: `${inning}${state}`.trim() || 'Live', color: theme.palette.success.main, live: true };
    }
    if (game.status) return { status: game.status, color: theme.palette.primary.main };
    return { status: 'Scheduled', color: theme.palette.primary.main };
  };

  const mlbData = game?.mlbGameData;
  // Prefer freshly-polled linescore from detailedStats over the stale game prop
  const linescore = detailedStats?.linescore || mlbData?.linescore;
  const homeTeam = game?.homeTeam;
  const awayTeam = game?.awayTeam;
  const gameStatus = getGameStatus();
  // Pre-game = not started yet. linescore can be {} from MLB API, so check innings array.
  const hasLinescoreData = linescore?.innings && linescore.innings.length > 0;
  const isPreGame = !game.isFinal && !game.isLive && !hasLinescoreData;

  // Auto-fetch detailed stats when boxscore is open
  useEffect(() => {
    if ((open || embedded) && game?.id) {
      fetchDetailedStats();
    }
  }, [open, embedded, game?.id, fetchDetailedStats]);

  // Auto-refresh on ALL tabs when the game is live
  // Tab 1 (play-by-play) polls faster; others poll every 15s
  useEffect(() => {
    if (!autoRefresh || !gameStatus.live) return;
    const hasLiveAb = detailedStatsRef.current?.currentPlay?.isInProgress;
    const intervalMs = currentTab === 1
      ? (hasLiveAb ? 5000 : 10000)
      : 15000;
    const interval = setInterval(() => fetchDetailedStats(true), intervalMs);
    return () => clearInterval(interval);
  }, [currentTab, autoRefresh, gameStatus.live, fetchDetailedStats]);

  // Fetch matchup data eagerly (used in pre-game view and Box tab)
  useEffect(() => {
    if (matchupData) return; // already fetched (key remount resets this)
    const awayAbbr = awayTeam?.abbreviation || detailedStats?.teams?.away?.abbreviation;
    const homeAbbr = homeTeam?.abbreviation || detailedStats?.teams?.home?.abbreviation;
    // Determine starting pitchers
    const homePitcher = game?.probableHomePitcher
      || detailedStats?.pitchingStats?.home?.[0]?.name
      || null;
    const awayPitcher = game?.probableAwayPitcher
      || detailedStats?.pitchingStats?.away?.[0]?.name
      || null;
    if (!awayAbbr || !homeAbbr || (!homePitcher && !awayPitcher)) return;
    const fetchMatchups = async () => {
      setMatchupLoading(true);
      try {
        const results = {};
        // Away lineup vs Home pitcher
        if (homePitcher) {
          try {
            const data = await matchupsApi.getGamePreview(awayAbbr, homeAbbr, `${homeAbbr}-${homePitcher.replace(/\s+/g, '_')}`);
            results.awayVsHomePitcher = data;
          } catch { /* no data */ }
        }
        // Home lineup vs Away pitcher
        if (awayPitcher) {
          try {
            const data = await matchupsApi.getGamePreview(homeAbbr, awayAbbr, `${awayAbbr}-${awayPitcher.replace(/\s+/g, '_')}`);
            results.homeVsAwayPitcher = data;
          } catch { /* no data */ }
        }
        setMatchupData(results);
      } catch (err) {
        console.error('Failed to fetch matchup data:', err);
      } finally {
        setMatchupLoading(false);
      }
    };
    fetchMatchups();
  }, [matchupData, detailedStats, awayTeam, homeTeam, game?.probableHomePitcher, game?.probableAwayPitcher]);

  // ── Handlers ──
  const handleInningClick = async (inning) => {
    // Fetch stats if not loaded yet
    if (!detailedStats) {
      setLoadingStats(true);
      await fetchDetailedStats();
      setLoadingStats(false);
    }
    setInningPopup({ inning });
  };

  const handlePlayerClick = (event, player, type) => {
    setPlayerPopup({ player, anchorEl: event.currentTarget, type });
  };

  const navigateToPlayer = (playerName) => {
    // Build slug: "José Altuve" → "jose-altuve"
    const slug = playerName
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // strip accents
      .replace(/[._]/g, '-')                            // dots/underscores → hyphens
      .replace(/\s+/g, '-')                              // spaces → hyphens
      .replace(/-+/g, '-')                               // collapse multiple hyphens
      .toLowerCase();
    window.location.href = `/players/profile/${slug}`;
  };

  // Early return guards — AFTER all hooks
  if (!game) {
    if (embedded) {
      return (
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography color="text.secondary">No game data available</Typography>
          </CardContent>
        </Card>
      );
    }
    return null;
  }
  if (!open && !embedded) return null;

  // ════════════════════════════════════════════════════════════════════════════
  // SUB-COMPONENTS
  // ════════════════════════════════════════════════════════════════════════════

  // ── Inning Detail Dialog ──
  const InningDetailDialog = () => {
    if (!inningPopup) return null;

    const { inning } = inningPopup;
    const plays = (detailedStats?.playByPlay || []).filter(p => p.inning === inning);
    const topPlays = plays.filter(p => p.halfInning === 'top');
    const bottomPlays = plays.filter(p => p.halfInning === 'bottom');

    const ordinal = (n) => {
      const s = ['th', 'st', 'nd', 'rd'];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const PlayRow = ({ play, idx }) => {
      const isScoring = play.rbi > 0;
      return (
        <Box
          sx={{
            py: 1,
            px: 1.5,
            borderLeft: isScoring ? `3px solid ${theme.palette.success.main}` : '3px solid transparent',
            bgcolor: isScoring ? alpha(theme.palette.success.main, 0.04) : 'transparent',
            '&:not(:last-child)': { borderBottom: `1px solid ${theme.palette.divider}` },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                <Typography variant="caption" fontWeight="bold" color="primary">
                  {play.batter}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  vs {play.pitcher}
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                {play.description}
              </Typography>
            </Box>
            <Stack alignItems="flex-end" spacing={0.25} sx={{ ml: 2, flexShrink: 0 }}>
              <Typography variant="caption" fontWeight="bold">
                {play.awayScore}–{play.homeScore}
              </Typography>
              {play.rbi > 0 && (
                <Chip label={`${play.rbi} RBI`} color="success" size="small"
                  sx={{ fontSize: '0.75rem', height: 18 }} />
              )}
            </Stack>
          </Stack>
        </Box>
      );
    };

    const HalfInning = ({ label, icon, plays: halfPlays, teamAbbr }) => (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          {icon}
          <Avatar src={getTeamLogoUrl(teamAbbr)} sx={{ width: 20, height: 20 }} />
          <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
          <Chip label={`${halfPlays.length} PA`} size="small" variant="outlined"
            sx={{ fontSize: '0.75rem', height: 20 }} />
        </Stack>
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          {halfPlays.length > 0 ? (
            halfPlays.map((play, idx) => <PlayRow key={idx} play={play} idx={idx} />)
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No plays recorded
            </Typography>
          )}
        </Paper>
      </Box>
    );

    return (
      <Dialog
        open={!!inningPopup}
        onClose={() => setInningPopup(null)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1}>
              <SportsBaseball color="primary" fontSize="small" />
              <Typography variant="h6" fontWeight="bold">
                {ordinal(inning)} Inning
              </Typography>
            </Stack>
            <IconButton onClick={() => setInningPopup(null)} size="small">
              <Close />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {loadingStats && !detailedStats ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
            </Box>
          ) : plays.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
              No plays recorded for this inning yet.
            </Typography>
          ) : (
            <>
              <HalfInning
                label={`Top ${ordinal(inning)} — ${awayTeam?.abbreviation} batting`}
                icon={<ArrowUpward fontSize="small" color="action" />}
                plays={topPlays}
                teamAbbr={awayTeam?.abbreviation}
              />
              <HalfInning
                label={`Bottom ${ordinal(inning)} — ${homeTeam?.abbreviation} batting`}
                icon={<ArrowDownward fontSize="small" color="action" />}
                plays={bottomPlays}
                teamAbbr={homeTeam?.abbreviation}
              />
            </>
          )}
        </DialogContent>
      </Dialog>
    );
  };

  // ── Player Popover ──
  const PlayerPopover = () => {
    if (!playerPopup) return null;
    const { player, anchorEl, type } = playerPopup;

    return (
      <Popover
        open={!!playerPopup}
        anchorEl={anchorEl}
        onClose={() => setPlayerPopup(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              minWidth: 280,
              maxWidth: 340,
              boxShadow: theme.shadows[8],
            }
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Avatar
              src={getPlayerHeadshotUrl(player.playerId)}
              sx={{ width: 56, height: 56, border: `2px solid ${theme.palette.divider}` }}
            />
            <Box>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ lineHeight: 1.2 }}>
                {player.name}
              </Typography>
              {player.position && (
                <Chip label={player.position} size="small" variant="outlined"
                  sx={{ mt: 0.5, fontSize: '0.75rem', height: 20 }} />
              )}
            </Box>
          </Stack>

          {/* Game stats */}
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ mb: 1, display: 'block' }}>
              GAME STATS
            </Typography>
            {type === 'batter' && player.stats ? (
              <Grid container spacing={1}>
                {[
                  { label: 'AB', value: player.stats.atBats },
                  { label: 'H', value: player.stats.hits },
                  { label: 'R', value: player.stats.runs },
                  { label: 'RBI', value: player.stats.rbi },
                  { label: 'BB', value: player.stats.baseOnBalls },
                  { label: 'SO', value: player.stats.strikeOuts },
                ].map(({ label, value }) => (
                  <Grid item xs={4} key={label}>
                    <Stack alignItems="center">
                      <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>
                        {value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            ) : type === 'pitcher' && player.stats ? (
              <Grid container spacing={1}>
                {[
                  { label: 'IP', value: player.stats.inningsPitched },
                  { label: 'H', value: player.stats.hits },
                  { label: 'ER', value: player.stats.earnedRuns },
                  { label: 'BB', value: player.stats.baseOnBalls },
                  { label: 'SO', value: player.stats.strikeOuts },
                  { label: 'PC', value: player.stats.pitchCount },
                ].map(({ label, value }) => (
                  <Grid item xs={4} key={label}>
                    <Stack alignItems="center">
                      <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1 }}>
                        {value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                    </Stack>
                  </Grid>
                ))}
              </Grid>
            ) : null}
          </Paper>

          {/* Actions */}
          <Button
            fullWidth
            variant="outlined"
            size="small"
            startIcon={<OpenInNew />}
            onClick={() => {
              setPlayerPopup(null);
              navigateToPlayer(player.name);
            }}
            sx={{ textTransform: 'none' }}
          >
            View Full Profile
          </Button>
        </Box>
      </Popover>
    );
  };

  // ── Clickable player name cell ──
  const PlayerNameCell = ({ player, type, sticky = false }) => (
    <TableCell sx={sticky ? { position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 } : {}}>
      <ButtonBase
        onClick={(e) => handlePlayerClick(e, player, type)}
        sx={{
          textAlign: 'left',
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          transition: 'all 0.15s',
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
          },
        }}
      >
        <Typography variant="body2" fontWeight="bold" sx={{ cursor: 'pointer' }}>
          {player.name}
        </Typography>
      </ButtonBase>
    </TableCell>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // LINE SCORE TABLE — clickable innings
  // ════════════════════════════════════════════════════════════════════════════
  const LineScoreTable = () => {
    if (!linescore?.innings) return null;

    const innings = linescore.innings;
    const maxInnings = Math.max(9, innings.length);

    return (
      <TableContainer component={Paper} sx={{ mb: 3, borderRadius: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
              <TableCell sx={{ fontWeight: 'bold', minWidth: isMobileView ? 60 : 140, position: 'sticky', left: 0, bgcolor: alpha(theme.palette.background.paper, 0.95), zIndex: 2 }}>Team</TableCell>
              {Array.from({ length: maxInnings }, (_, i) => {
                const inning = innings.find(inn => inn.num === i + 1);
                const hasPlays = !!inning;
                return (
                  <TableCell
                    key={i + 1}
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      minWidth: isMobileView ? 28 : 36,
                      px: isMobileView ? 0.5 : 1,
                      cursor: hasPlays ? 'pointer' : 'default',
                      transition: 'all 0.15s',
                      userSelect: 'none',
                      '&:hover': hasPlays ? {
                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                      } : {},
                    }}
                    onClick={() => hasPlays && handleInningClick(i + 1)}
                  >
                    <Tooltip title={hasPlays ? `View ${i + 1}${['st','nd','rd'][i] || 'th'} inning plays` : ''} arrow>
                      <span>{i + 1}</span>
                    </Tooltip>
                  </TableCell>
                );
              })}
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.1) }}>R</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.1) }}>H</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.1) }}>E</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Away Team Row */}
            <TableRow sx={{ '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) } }}>
              <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar src={getTeamLogoUrl(awayTeam?.abbreviation)} sx={{ width: 24, height: 24 }} />
                  {!isMobileView && (
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {awayTeam?.name || awayTeam?.abbreviation}
                    </Typography>
                  </Box>
                  )}
                  {isMobileView && (
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                      {awayTeam?.abbreviation}
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              {Array.from({ length: maxInnings }, (_, i) => {
                const inning = innings.find(inn => inn.num === i + 1);
                const runs = inning?.away?.runs;
                const scored = runs > 0;
                return (
                  <TableCell
                    key={i + 1}
                    align="center"
                    sx={{
                      cursor: inning ? 'pointer' : 'default',
                      fontWeight: scored ? 'bold' : 'normal',
                      color: scored ? theme.palette.primary.main : 'inherit',
                      transition: 'all 0.15s',
                      px: isMobileView ? 0.5 : 1,
                      minWidth: isMobileView ? 28 : 36,
                      '&:hover': inning ? {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      } : {},
                    }}
                    onClick={() => inning && handleInningClick(i + 1)}
                  >
                    {runs ?? '-'}
                  </TableCell>
                );
              })}
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                {linescore.teams?.away?.runs || game.awayScore || 0}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                {linescore.teams?.away?.hits || 0}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                {linescore.teams?.away?.errors || 0}
              </TableCell>
            </TableRow>

            {/* Home Team Row */}
            <TableRow sx={{ '&:hover': { bgcolor: alpha(theme.palette.action.hover, 0.04) } }}>
              <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: 'background.paper', zIndex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar src={getTeamLogoUrl(homeTeam?.abbreviation)} sx={{ width: 24, height: 24 }} />
                  {!isMobileView && (
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {homeTeam?.name || homeTeam?.abbreviation}
                    </Typography>
                  </Box>
                  )}
                  {isMobileView && (
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                      {homeTeam?.abbreviation}
                    </Typography>
                  )}
                </Stack>
              </TableCell>
              {Array.from({ length: maxInnings }, (_, i) => {
                const inning = innings.find(inn => inn.num === i + 1);
                const runs = inning?.home?.runs;
                const scored = runs > 0;
                return (
                  <TableCell
                    key={i + 1}
                    align="center"
                    sx={{
                      cursor: inning ? 'pointer' : 'default',
                      fontWeight: scored ? 'bold' : 'normal',
                      color: scored ? theme.palette.primary.main : 'inherit',
                      transition: 'all 0.15s',
                      px: isMobileView ? 0.5 : 1,
                      minWidth: isMobileView ? 28 : 36,
                      '&:hover': inning ? {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                      } : {},
                    }}
                    onClick={() => inning && handleInningClick(i + 1)}
                  >
                    {runs ?? '-'}
                  </TableCell>
                );
              })}
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                {linescore.teams?.home?.runs || game.homeScore || 0}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                {linescore.teams?.home?.hits || 0}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                {linescore.teams?.home?.errors || 0}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // BATTING STATS — clickable player names
  // ════════════════════════════════════════════════════════════════════════════
  const BattingStats = () => {
    if (loadingStats) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        </Box>
      );
    }

    if (!detailedStats?.battingStats) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Groups sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">Batting Statistics Unavailable</Typography>
        </Box>
      );
    }

    const renderBattingTable = (players, teamAbbr, teamName) => (
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Avatar src={getTeamLogoUrl(teamAbbr)} sx={{ width: 24, height: 24 }} />
          <Typography variant={isMobileView ? 'subtitle1' : 'h6'} fontWeight="bold" color="primary">
            {isMobileView ? teamAbbr : (teamName || teamAbbr)} Batting
          </Typography>
        </Stack>
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: alpha(theme.palette.background.paper, 0.95), zIndex: 2, minWidth: isMobileView ? 90 : 'auto' }}>Player</TableCell>
                {!isMobileView && <TableCell align="center" sx={{ fontWeight: 'bold' }}>Pos</TableCell>}
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>AB</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>R</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>H</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>RBI</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>BB</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>SO</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>AVG</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players
                .filter(p => p.stats.atBats > 0 || p.battingOrder)
                .sort((a, b) => parseInt(a.battingOrder || '999') - parseInt(b.battingOrder || '999'))
                .map((player) => {
                  const hadHit = player.stats.hits > 0;
                  return (
                    <TableRow
                      key={player.playerId}
                      sx={{
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                        ...(hadHit && { bgcolor: alpha(theme.palette.success.main, 0.03) }),
                      }}
                    >
                      <PlayerNameCell player={player} type="batter" sticky={isMobileView} />
                      {!isMobileView && (
                      <TableCell align="center">
                        <Chip label={player.position} size="small" variant="outlined"
                          sx={{ fontSize: '0.75rem', height: 20, minWidth: 28 }} />
                      </TableCell>
                      )}
                      <TableCell align="center">{player.stats.atBats}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: player.stats.runs > 0 ? 'bold' : 'normal' }}>
                        {player.stats.runs}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: hadHit ? 'bold' : 'normal', color: hadHit ? theme.palette.success.dark : 'inherit' }}>
                        {player.stats.hits}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: player.stats.rbi > 0 ? 'bold' : 'normal' }}>
                        {player.stats.rbi}
                      </TableCell>
                      <TableCell align="center">{player.stats.baseOnBalls}</TableCell>
                      <TableCell align="center">{player.stats.strikeOuts}</TableCell>
                      <TableCell align="center">{player.stats.avg}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );

    return (
      <Box>
        {renderBattingTable(detailedStats.battingStats.away, awayTeam?.abbreviation, awayTeam?.name)}
        {renderBattingTable(detailedStats.battingStats.home, homeTeam?.abbreviation, homeTeam?.name)}
      </Box>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // PITCHING STATS — clickable player names
  // ════════════════════════════════════════════════════════════════════════════
  const PitchingStats = () => {
    if (loadingStats) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
        </Box>
      );
    }

    if (!detailedStats?.pitchingStats) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Assessment sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">Pitching Statistics Unavailable</Typography>
        </Box>
      );
    }

    const renderPitchingTable = (players, teamAbbr, teamName) => (
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <Avatar src={getTeamLogoUrl(teamAbbr)} sx={{ width: 24, height: 24 }} />
          <Typography variant={isMobileView ? 'subtitle1' : 'h6'} fontWeight="bold" color="primary">
            {isMobileView ? teamAbbr : (teamName || teamAbbr)} Pitching
          </Typography>
        </Stack>
        <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: alpha(theme.palette.background.paper, 0.95), zIndex: 2, minWidth: isMobileView ? 90 : 'auto' }}>Pitcher</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>IP</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>H</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>R</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>ER</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>BB</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>SO</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>PC</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>ERA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {players
                .filter(p => parseFloat(p.stats.inningsPitched) > 0)
                .map((player) => {
                  const cleanLine = parseInt(player.stats.earnedRuns) === 0;
                  return (
                    <TableRow
                      key={player.playerId}
                      sx={{
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                        ...(cleanLine && { bgcolor: alpha(theme.palette.success.main, 0.03) }),
                      }}
                    >
                      <PlayerNameCell player={player} type="pitcher" sticky={isMobileView} />
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>{player.stats.inningsPitched}</TableCell>
                      <TableCell align="center">{player.stats.hits}</TableCell>
                      <TableCell align="center">{player.stats.runs}</TableCell>
                      <TableCell align="center">{player.stats.earnedRuns}</TableCell>
                      <TableCell align="center">{player.stats.baseOnBalls}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: player.stats.strikeOuts > 0 ? 'bold' : 'normal' }}>
                        {player.stats.strikeOuts}
                      </TableCell>
                      <TableCell align="center">{player.stats.pitchCount}</TableCell>
                      <TableCell align="center">{player.stats.era}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );

    return (
      <Box>
        {renderPitchingTable(detailedStats.pitchingStats.away, awayTeam?.abbreviation, awayTeam?.name)}
        {renderPitchingTable(detailedStats.pitchingStats.home, homeTeam?.abbreviation, homeTeam?.name)}
      </Box>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // PLAY BY PLAY  —  expandable at-bat cards with pitch location chart
  // ════════════════════════════════════════════════════════════════════════════
  const PlayByPlay = () => {

    if (loadingStats) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
        </Box>
      );
    }

    if (!detailedStats?.playByPlay || detailedStats.playByPlay.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Timeline sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">Play by Play Unavailable</Typography>
        </Box>
      );
    }

    // ── Pitch-call colour map ──
    const getPitchColor = (callCode) => {
      if (!callCode) return '#9e9e9e';
      switch (callCode) {
        case 'B': return '#4caf50';                       // Ball
        case 'C': return '#f44336';                       // Called strike
        case 'S': case 'W': case 'T': case 'M':          // Swinging / missed
          return '#ff9800';
        case 'F': case 'R': case 'L':                    // Foul
          return '#ffc107';
        case 'X': case 'D': case 'E':                    // In play
          return '#2196f3';
        default: return '#9e9e9e';
      }
    };

    // ── Strike-zone SVG ──
    const StrikeZoneChart = ({ pitches }) => {
      if (!pitches || pitches.length === 0) return null;

      const width = 200, height = 240, pad = 20;
      const szLeft = -0.83, szRight = 0.83;
      const szTopVal = pitches[0]?.szTop || 3.5;
      const szBotVal = pitches[0]?.szBot || 1.5;
      const xMin = -2.5, xMax = 2.5, yMin = 0, yMax = 5;

      const sx = (x) => pad + ((x - xMin) / (xMax - xMin)) * (width - 2 * pad);
      const sy = (y) => height - pad - ((y - yMin) / (yMax - yMin)) * (height - 2 * pad);

      return (
        <svg width={width} height={height}
          style={{ background: alpha(theme.palette.background.default, 0.5),
                   borderRadius: 8, border: `1px solid ${theme.palette.divider}` }}>
          {/* Strike zone rectangle */}
          <rect x={sx(szLeft)} y={sy(szTopVal)}
            width={sx(szRight) - sx(szLeft)} height={sy(szBotVal) - sy(szTopVal)}
            fill="none" stroke={theme.palette.text.secondary}
            strokeWidth={1.5} strokeDasharray="4,2" />

          {/* 3×3 grid lines */}
          {[1/3, 2/3].map(f => (
            <React.Fragment key={`g-${f}`}>
              <line x1={sx(szLeft + f * (szRight - szLeft))} y1={sy(szTopVal)}
                    x2={sx(szLeft + f * (szRight - szLeft))} y2={sy(szBotVal)}
                    stroke={alpha(theme.palette.text.secondary, 0.15)} strokeWidth={0.5} />
              <line x1={sx(szLeft)} y1={sy(szBotVal + f * (szTopVal - szBotVal))}
                    x2={sx(szRight)} y2={sy(szBotVal + f * (szTopVal - szBotVal))}
                    stroke={alpha(theme.palette.text.secondary, 0.15)} strokeWidth={0.5} />
            </React.Fragment>
          ))}

          {/* Home plate */}
          <polygon
            points={`${sx(0)},${sy(0)+3} ${sx(-0.4)},${sy(0)-3} ${sx(-0.7)},${sy(0)-8} ${sx(0.7)},${sy(0)-8} ${sx(0.4)},${sy(0)-3}`}
            fill={alpha(theme.palette.text.secondary, 0.15)}
            stroke={theme.palette.text.secondary} strokeWidth={1} />

          {/* Pitch dots + numbers */}
          {pitches.map((p, i) => {
            if (p.pX == null || p.pZ == null) return null;
            return (
              <g key={i}>
                <circle cx={sx(p.pX)} cy={sy(p.pZ)} r={7}
                  fill={getPitchColor(p.callCode)} stroke="white" strokeWidth={1.5} opacity={0.9} />
                <text x={sx(p.pX)} y={sy(p.pZ) + 3.5} textAnchor="middle"
                  fontSize={8} fill="white" fontWeight="bold">
                  {p.pitchNumber || i + 1}
                </text>
              </g>
            );
          })}
        </svg>
      );
    };

    // ── Inline diamond for runners ──
    const BaseDiamond = ({ runners, size = 36 }) => {
      const onFirst  = runners?.some(r => r.endBase === '1B' && !r.isOut);
      const onSecond = runners?.some(r => r.endBase === '2B' && !r.isOut);
      const onThird  = runners?.some(r => r.endBase === '3B' && !r.isOut);

      return (
        <svg width={size} height={size} viewBox="0 0 36 36">
          <rect x="13" y="1" width="10" height="10" rx="1" transform="rotate(45,18,6)"
            fill={onSecond ? theme.palette.warning.main : 'transparent'}
            stroke={theme.palette.text.secondary} strokeWidth={1.5} />
          <rect x="3" y="11" width="10" height="10" rx="1" transform="rotate(45,8,16)"
            fill={onThird ? theme.palette.warning.main : 'transparent'}
            stroke={theme.palette.text.secondary} strokeWidth={1.5} />
          <rect x="23" y="11" width="10" height="10" rx="1" transform="rotate(45,28,16)"
            fill={onFirst ? theme.palette.warning.main : 'transparent'}
            stroke={theme.palette.text.secondary} strokeWidth={1.5} />
        </svg>
      );
    };

    const sortedPlays = [...detailedStats.playByPlay];
    if (playByPlaySort === 'recent') sortedPlays.reverse();

    return (
      <Box>
        {/* ── Controls ── */}
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" color="primary" fontWeight="bold">Play by Play</Typography>
            <AnimatePresence>
              {newPlaysCount > 0 && (
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}>
                  <Chip label={`${newPlaysCount} new play${newPlaysCount > 1 ? 's' : ''}!`}
                    color="success" size="small" sx={{ mt: 0.5 }} />
                </motion.div>
              )}
            </AnimatePresence>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Order</InputLabel>
              <Select value={playByPlaySort} label="Order"
                onChange={(e) => setPlayByPlaySort(e.target.value)}>
                <MenuItem value="recent">Recent First</MenuItem>
                <MenuItem value="chronological">Inning 1–9</MenuItem>
              </Select>
            </FormControl>

            {gameStatus.live && (
              <Button size="small" variant={autoRefresh ? 'contained' : 'outlined'}
                onClick={() => setAutoRefresh(!autoRefresh)}>
                {autoRefresh ? 'LIVE' : 'Paused'}
              </Button>
            )}

            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchDetailedStats(true)} size="small" color="primary">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* ── Pitch legend ── */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          {[
            { label: 'Ball', color: '#4caf50' },
            { label: 'Called Strike', color: '#f44336' },
            { label: 'Swinging', color: '#ff9800' },
            { label: 'Foul', color: '#ffc107' },
            { label: 'In Play', color: '#2196f3' },
          ].map(item => (
            <Stack key={item.label} direction="row" alignItems="center" spacing={0.5}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, border: '1px solid rgba(255,255,255,0.4)' }} />
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            </Stack>
          ))}
        </Stack>

        {/* ── Live current AB card (in-progress OR just completed) ── */}
        {gameStatus.live && detailedStats?.currentPlay && (() => {
          const cp = detailedStats.currentPlay;
          // Deduplicate: hide if this exact AB already appears as top completed play
          const lastCompleted = detailedStats.playByPlay?.slice(-1)[0];
          const alreadyInList =
            !cp.isInProgress &&
            lastCompleted &&
            lastCompleted.batter === cp.batter &&
            lastCompleted.inning === cp.inning &&
            lastCompleted.halfInning === cp.halfInning;
          if (alreadyInList) return null;

          const halfLabel = cp.halfInning === 'bottom' ? '▼' : '▲';
          const inningLabel = cp.inning ? `${halfLabel} ${cp.inning}` : '';
          const borderColor = cp.isInProgress ? theme.palette.success.main : theme.palette.warning.main;
          return (
            <Card sx={{
              borderRadius: 2,
              border: `2px solid ${borderColor}`,
              bgcolor: alpha(borderColor, 0.06),
              mb: 1,
              overflow: 'hidden',
              position: 'relative',
            }}>
              {/* Live / just-completed indicator */}
              <Box sx={{
                position: 'absolute', top: 8, right: 8,
                display: 'flex', alignItems: 'center', gap: 0.5,
              }}>
                <Box sx={{
                  width: 8, height: 8, borderRadius: '50%',
                  bgcolor: cp.isInProgress ? 'success.main' : 'warning.main',
                  ...(cp.isInProgress && {
                    animation: 'pulse 1.4s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.25 },
                    },
                  }),
                }} />
                <Typography variant="caption" color={cp.isInProgress ? 'success.main' : 'warning.main'} fontWeight="bold">
                  {cp.isInProgress ? 'LIVE AB' : 'LAST AB'}
                </Typography>
              </Box>

              <Box sx={{ p: 1.5, pr: 8 }}>
                {/* Inning + count */}
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="bold">{inningLabel}</Typography>
                  <Typography variant="caption" color="text.secondary">·</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {cp.count?.balls ?? 0}-{cp.count?.strikes ?? 0} · {cp.count?.outs ?? 0} out{cp.count?.outs !== 1 ? 's' : ''}
                  </Typography>
                </Stack>

                {/* Matchup */}
                <Stack direction="row" alignItems="baseline" spacing={0.75} sx={{ mb: cp.isInProgress ? 1 : 0.5 }}>
                  <Typography variant="body2" fontWeight="bold">{cp.batter}</Typography>
                  <Typography variant="caption" color="text.secondary">vs</Typography>
                  <Typography variant="body2" color="text.secondary">{cp.pitcher}</Typography>
                </Stack>

                {/* Result line for just-completed AB */}
                {!cp.isInProgress && cp.event && (
                  <Typography variant="caption" color="warning.main" fontWeight="bold" sx={{ display: 'block', mb: 1 }}>
                    {cp.event}
                  </Typography>
                )}

                {/* Pitches thrown this AB */}
                {cp.pitches?.length > 0 && (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {cp.pitches.map((p, i) => (
                      <Tooltip key={i} title={`${p.typeDesc || p.type || '?'} — ${p.call || '?'}${p.speed ? ` @ ${Math.round(p.speed)} mph` : ''}`} arrow>
                        <Box sx={{
                          width: 22, height: 22, borderRadius: '50%',
                          bgcolor: (() => {
                            const c = p.callCode;
                            if (c === 'B') return '#4caf50';
                            if (c === 'C') return '#f44336';
                            if (['S','W','T','M'].includes(c)) return '#ff9800';
                            if (['F','R','L'].includes(c)) return '#ffc107';
                            if (['X','D','E'].includes(c)) return '#2196f3';
                            return '#9e9e9e';
                          })(),
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: '1.5px solid rgba(255,255,255,0.35)',
                          cursor: 'default',
                        }}>
                          <Typography variant="caption" sx={{ fontSize: 9, fontWeight: 'bold', color: '#fff', lineHeight: 1 }}>
                            {p.pitchNumber || i + 1}
                          </Typography>
                        </Box>
                      </Tooltip>
                    ))}
                  </Stack>
                )}
              </Box>
            </Card>
          );
        })()}

        {/* ── At-bat cards ── */}
        <Stack spacing={1}>
          {sortedPlays.slice(0, 100).map((play, index) => {
            const isExpanded = expandedPlay === index;
            const isNew = playByPlaySort === 'recent' && newPlaysCount > 0 && index < newPlaysCount;
            const isScoring = play.rbi > 0;

            return (
              <Card
                key={`${play.inning}-${play.halfInning}-${index}`}
                component={motion.div}
                initial={isNew ? { borderColor: theme.palette.success.main } : false}
                animate={isNew ? { borderColor: 'transparent' } : false}
                transition={{ duration: 2 }}
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${isScoring ? alpha(theme.palette.success.main, 0.3) : theme.palette.divider}`,
                  ...(isNew && { borderLeft: `3px solid ${theme.palette.success.main}` }),
                  ...(isScoring && { bgcolor: alpha(theme.palette.success.main, 0.02) }),
                  cursor: 'pointer',
                  '&:hover': { borderColor: alpha(theme.palette.primary.main, 0.5) },
                }}
              >
                {/* ── Collapsed summary row ── */}
                <ButtonBase
                  onClick={() => setExpandedPlay(isExpanded ? null : index)}
                  sx={{ width: '100%', display: 'block', textAlign: 'left', p: 1.5, borderRadius: 2 }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
                      <Chip
                        label={`${play.halfInning === 'top' ? '▲' : '▼'} ${play.inning}`}
                        size="small"
                        sx={{ fontWeight: 'bold', minWidth: 48, fontSize: '0.75rem' }}
                      />
                      <Avatar src={getPlayerHeadshotUrl(play.batterId)} sx={{ width: 28, height: 28 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold" noWrap>
                          {play.batter}
                          {!isMobileView && (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                              vs {play.pitcher}
                            </Typography>
                          )}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                          {play.description}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" alignItems="center" spacing={1}>
                      {play.rbi > 0 && (
                        <Chip label={`${play.rbi} RBI`} size="small" color="success"
                          sx={{ fontSize: '0.75rem', height: 20 }} />
                      )}
                      {isNew && <Chip label="NEW" size="small" color="success" sx={{ fontSize: '0.75rem', height: 16 }} />}
                      <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 40, textAlign: 'right' }}>
                        {play.awayScore}–{play.homeScore}
                      </Typography>
                      {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                    </Stack>
                  </Stack>
                </ButtonBase>

                {/* ── Expanded detail ── */}
                <Collapse in={isExpanded} unmountOnExit>
                  <Divider />
                  <Box sx={{ p: 2 }}>
                    {/* Batter vs Pitcher header */}
                    <Stack direction="row" alignItems="center" justifyContent="center" spacing={3} sx={{ mb: 2 }}>
                      <Stack alignItems="center" spacing={0.5}>
                        <Avatar src={getPlayerHeadshotUrl(play.batterId)} sx={{ width: 48, height: 48 }} />
                        <Typography variant="body2" fontWeight="bold">{play.batter}</Typography>
                        <Chip label="BATTER" size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 18 }} />
                      </Stack>
                      <Typography variant="h6" color="text.secondary">vs</Typography>
                      <Stack alignItems="center" spacing={0.5}>
                        <Avatar src={getPlayerHeadshotUrl(play.pitcherId)} sx={{ width: 48, height: 48 }} />
                        <Typography variant="body2" fontWeight="bold">{play.pitcher}</Typography>
                        <Chip label="PITCHER" size="small" variant="outlined" sx={{ fontSize: '0.75rem', height: 18 }} />
                      </Stack>
                    </Stack>

                    {/* Result chip + count */}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Chip label={play.event || play.description} color={isScoring ? 'success' : 'default'} />
                      {play.count && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          Count: {play.count.balls}-{play.count.strikes}, {play.count.outs} out{play.count.outs !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Box>

                    {/* Pitch chart + pitch-by-pitch table */}
                    {play.pitches && play.pitches.length > 0 && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={5} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                          <StrikeZoneChart pitches={play.pitches} />
                        </Grid>

                        <Grid item xs={12} sm={7} md={8}>
                          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                                  <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>#</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Pitch</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.5 }}>MPH</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold', py: 0.5 }}>Result</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.5 }}>Count</TableCell>
                                  {!isMobileView && <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.5 }}>hB</TableCell>}
                                  {!isMobileView && <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.5 }}>vB</TableCell>}
                                  {!isMobileView && <TableCell align="center" sx={{ fontWeight: 'bold', py: 0.5 }}>Spin</TableCell>}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {play.pitches.map((p, pi) => (
                                  <TableRow key={pi} sx={{ '&:last-child td': { border: 0 } }}>
                                    <TableCell sx={{ py: 0.5 }}>
                                      <Box sx={{
                                        width: 20, height: 20, borderRadius: '50%',
                                        bgcolor: getPitchColor(p.callCode),
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      }}>
                                        <Typography variant="caption"
                                          sx={{ color: 'white', fontSize: '0.75rem', fontWeight: 'bold', lineHeight: 1 }}>
                                          {p.pitchNumber || pi + 1}
                                        </Typography>
                                      </Box>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        {p.typeDesc || p.type || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.5 }}>
                                      <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.75rem' }}>
                                        {p.speed ? Math.round(p.speed) : '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ py: 0.5 }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        {p.call || '—'}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="center" sx={{ py: 0.5 }}>
                                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                        {p.count ? `${p.count.balls}-${p.count.strikes}` : '—'}
                                      </Typography>
                                    </TableCell>
                                    {!isMobileView && (
                                      <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                          {p.hB != null ? `${p.hB > 0 ? '+' : ''}${p.hB.toFixed(1)}"` : '—'}
                                        </Typography>
                                      </TableCell>
                                    )}
                                    {!isMobileView && (
                                      <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                          {p.vB != null ? `${p.vB > 0 ? '+' : ''}${p.vB.toFixed(1)}"` : '—'}
                                        </Typography>
                                      </TableCell>
                                    )}
                                    {!isMobileView && (
                                      <TableCell align="center" sx={{ py: 0.5 }}>
                                        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                          {p.spin != null ? p.spin : '—'}
                                        </Typography>
                                      </TableCell>
                                    )}
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Grid>
                      </Grid>
                    )}

                    {/* Hit location field */}
                    {play.hitData?.coordX != null && play.hitData?.coordY != null && (() => {
                      const ev = (play.event || '').toLowerCase();
                      let dotColor;
                      if (ev.includes('home run')) dotColor = '#e74c3c';
                      else if (ev.includes('triple')) dotColor = '#9b59b6';
                      else if (ev.includes('double')) dotColor = '#3498db';
                      else if (ev.includes('single')) dotColor = '#2ecc71';
                      else dotColor = alpha(theme.palette.text.secondary, 0.5);
                      const hd = play.hitData;
                      return (
                        <Box sx={{ mt: 2, p: 1.5, borderRadius: 1, bgcolor: alpha(theme.palette.background.default, 0.5), border: `1px solid ${theme.palette.divider}` }}>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Box sx={{ flexShrink: 0 }}>
                              <svg viewBox="0 0 250 250" width={isMobileView ? 90 : 110} style={{ display: 'block' }}>
                                {/* Outfield grass */}
                                <path d="M125,210 L20,105 A150,150 0 0,1 230,105 Z"
                                  fill={alpha('#4caf50', 0.06)} stroke={alpha('#4caf50', 0.2)} strokeWidth="0.8" />
                                {/* Infield diamond */}
                                <polygon points="125,185 155,160 125,135 95,160"
                                  fill="none" stroke={alpha('#8d6e63', 0.25)} strokeWidth="0.6" />
                                {/* Foul lines */}
                                <line x1="125" y1="210" x2="20" y2="105" stroke={alpha('#fff', 0.08)} strokeWidth="0.4" />
                                <line x1="125" y1="210" x2="230" y2="105" stroke={alpha('#fff', 0.08)} strokeWidth="0.4" />
                                {/* Hit dot */}
                                <circle cx={hd.coordX} cy={hd.coordY} r={7}
                                  fill={dotColor} stroke="white" strokeWidth={1.5} />
                              </svg>
                            </Box>
                            <Stack spacing={0.5} sx={{ minWidth: 0 }}>
                              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                                {play.event || 'Ball In Play'}
                              </Typography>
                              {hd.exitVelocity != null && (
                                <Typography variant="caption" color="text.secondary">
                                  Exit Velo: <b>{hd.exitVelocity} mph</b>
                                </Typography>
                              )}
                              {hd.launchAngle != null && (
                                <Typography variant="caption" color="text.secondary">
                                  Launch Angle: <b>{hd.launchAngle}°</b>
                                </Typography>
                              )}
                              {hd.distance != null && (
                                <Typography variant="caption" color="text.secondary">
                                  Distance: <b>{hd.distance} ft</b>
                                </Typography>
                              )}
                              {hd.trajectory && (
                                <Chip label={hd.trajectory} size="small" variant="outlined"
                                  sx={{ fontSize: '0.65rem', height: 18, alignSelf: 'flex-start' }} />
                              )}
                            </Stack>
                          </Stack>
                        </Box>
                      );
                    })()}

                    {/* Runners */}
                    {play.runners && play.runners.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <BaseDiamond runners={play.runners} />
                          <Stack spacing={0.25}>
                            {play.runners.filter(r => r.event).map((r, ri) => (
                              <Typography key={ri} variant="caption" color="text.secondary">
                                {r.name}: {r.startBase || 'Batter'} → {r.endBase || (r.isOut ? 'Out' : 'Scored')}
                                {r.isScoringEvent && ' ⚡'}
                              </Typography>
                            ))}
                          </Stack>
                        </Stack>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Card>
            );
          })}
        </Stack>

        {sortedPlays.length > 100 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            Showing 100 of {sortedPlays.length} plays
          </Typography>
        )}
      </Box>
    );
  };

  // ════════════════════════════════════════════════════════════════════════════
  // COMPACT SCORE HEADER — always visible, MLB-style
  // ════════════════════════════════════════════════════════════════════════════
  const ScoreHeader = () => (
    <Box sx={{ py: 1.5, mb: 1 }}>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={isMobileView ? 1.5 : 3}>
        {/* Away */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, justifyContent: 'flex-end' }}>
          <Typography variant="body1" fontWeight="bold" noWrap>
            {isMobileView ? awayTeam?.abbreviation : (awayTeam?.name || awayTeam?.abbreviation)}
          </Typography>
          <Avatar src={getTeamLogoUrl(awayTeam?.abbreviation)} sx={{ width: 36, height: 36 }} />
        </Stack>

        {/* Score / status center */}
        <Stack alignItems="center" sx={{ minWidth: 80 }}>
          {isPreGame ? (
            <>
              <Typography variant="h6" fontWeight="bold" color="text.secondary">
                {formatGameTime(game.gameTime || game.gameDate)}
              </Typography>
              <Chip label={gameStatus.status} size="small"
                sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
            </>
          ) : (
            <>
              <Stack direction="row" alignItems="baseline" spacing={1.5}>
                {(() => {
                  const awayR = linescore?.teams?.away?.runs ?? game.awayScore ?? 0;
                  const homeR = linescore?.teams?.home?.runs ?? game.homeScore ?? 0;
                  return (
                    <>
                      <Typography variant="h4" fontWeight="bold"
                        sx={{ color: awayR >= homeR ? 'primary.main' : 'text.primary' }}>
                        {awayR}
                      </Typography>
                      <Typography variant="body1" color="text.secondary">–</Typography>
                      <Typography variant="h4" fontWeight="bold"
                        sx={{ color: homeR >= awayR ? 'primary.main' : 'text.primary' }}>
                        {homeR}
                      </Typography>
                    </>
                  );
                })()}
              </Stack>
              <Chip label={gameStatus.status} size="small"
                color={gameStatus.live ? 'success' : 'default'}
                sx={{ fontWeight: 'bold', height: 20, fontSize: '0.7rem' }} />
            </>
          )}
        </Stack>

        {/* Home */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          <Avatar src={getTeamLogoUrl(homeTeam?.abbreviation)} sx={{ width: 36, height: 36 }} />
          <Typography variant="body1" fontWeight="bold" noWrap>
            {isMobileView ? homeTeam?.abbreviation : (homeTeam?.name || homeTeam?.abbreviation)}
          </Typography>
        </Stack>
      </Stack>

      {/* Venue + date */}
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 0.5 }}>
        {game.venue && (
          <Typography variant="caption" color="text.secondary">
            <Stadium sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
            {game.venue}
          </Typography>
        )}
        {isPreGame && (
          <>
            <Typography variant="caption" color="text.secondary">•</Typography>
            <Typography variant="caption" color="text.secondary">
              {formatGameDate(game.gameDate)}
            </Typography>
          </>
        )}
      </Stack>
    </Box>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // MLB-STYLE LIVE AT-BAT TRACKER
  // Diamond + count + strike zone with pitches + hit location on field
  // ════════════════════════════════════════════════════════════════════════════
  const CurrentSituation = () => {
    if (!linescore?.defense || !linescore?.offense) {
      // For final games, show the last at-bat result if hitData exists
      if (!game.isFinal) return null;
    }

    const balls = linescore?.balls ?? 0;
    const strikes = linescore?.strikes ?? 0;
    const outs = linescore?.outs ?? 0;
    const onFirst = !!linescore?.offense?.first;
    const onSecond = !!linescore?.offense?.second;
    const onThird = !!linescore?.offense?.third;
    const batter = linescore?.offense?.batter;
    const pitcher = linescore?.defense?.pitcher;

    // Find current/last at-bat from play-by-play
    const currentAB = detailedStats?.playByPlay?.slice(-1)[0];
    const abPitches = currentAB?.pitches || [];
    const hitData = currentAB?.hitData;
    const ballInPlay = hitData?.coordX != null && hitData?.coordY != null;
    const isHit = currentAB?.event && ['Single', 'Double', 'Triple', 'Home Run'].includes(currentAB.event);

    // Pitch color helper
    const getPitchDotColor = (callCode) => {
      if (!callCode) return '#9e9e9e';
      switch (callCode) {
        case 'B': return '#4caf50';
        case 'C': return '#f44336';
        case 'S': case 'W': case 'T': case 'M': return '#ff9800';
        case 'F': case 'R': case 'L': return '#ffc107';
        case 'X': case 'D': case 'E': return '#2196f3';
        default: return '#9e9e9e';
      }
    };

    // Inline mini diamond
    const Diamond = ({ size = 56 }) => {
      const cx = size / 2, cy = size / 2 + 3;
      const r = size * 0.32;
      const bases = [
        { x: cx + r, y: cy, on: onFirst },
        { x: cx, y: cy - r, on: onSecond },
        { x: cx - r, y: cy, on: onThird },
      ];
      const bs = size * 0.13;
      return (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <line x1={cx} y1={cy + r * 0.6} x2={cx + r} y2={cy} stroke={alpha(theme.palette.text.secondary, 0.25)} strokeWidth={1} />
          <line x1={cx + r} y1={cy} x2={cx} y2={cy - r} stroke={alpha(theme.palette.text.secondary, 0.25)} strokeWidth={1} />
          <line x1={cx} y1={cy - r} x2={cx - r} y2={cy} stroke={alpha(theme.palette.text.secondary, 0.25)} strokeWidth={1} />
          <line x1={cx - r} y1={cy} x2={cx} y2={cy + r * 0.6} stroke={alpha(theme.palette.text.secondary, 0.25)} strokeWidth={1} />
          {bases.map((b, i) => (
            <rect key={i} x={b.x - bs / 2} y={b.y - bs / 2} width={bs} height={bs}
              transform={`rotate(45, ${b.x}, ${b.y})`}
              fill={b.on ? theme.palette.warning.main : alpha(theme.palette.text.secondary, 0.12)}
              stroke={b.on ? theme.palette.warning.dark : alpha(theme.palette.text.secondary, 0.3)}
              strokeWidth={1} />
          ))}
          <polygon
            points={`${cx},${cy + r * 0.6 + 2} ${cx - 4},${cy + r * 0.6 - 2} ${cx - 3},${cy + r * 0.6 - 5} ${cx + 3},${cy + r * 0.6 - 5} ${cx + 4},${cy + r * 0.6 - 2}`}
            fill={alpha(theme.palette.text.secondary, 0.2)} />
        </svg>
      );
    };

    // Count dots
    const CountDots = ({ count, max, activeColor }) => (
      <Stack direction="row" spacing={0.3}>
        {Array.from({ length: max }, (_, i) => (
          <Box key={i} sx={{ width: 9, height: 9, borderRadius: '50%',
            bgcolor: i < count ? activeColor : alpha(theme.palette.text.secondary, 0.15) }} />
        ))}
      </Stack>
    );

    // Mini strike zone with pitches
    const MiniStrikeZone = () => {
      if (abPitches.length === 0) return null;
      const w = 120, h = 140, pad = 15;
      const szLeft = -0.83, szRight = 0.83, szTopVal = abPitches[0]?.szTop || 3.5, szBotVal = abPitches[0]?.szBot || 1.5;
      const xMin = -2, xMax = 2, yMin = 0.5, yMax = 4.5;
      const sx = (x) => pad + ((x - xMin) / (xMax - xMin)) * (w - 2 * pad);
      const sy = (y) => h - pad - ((y - yMin) / (yMax - yMin)) * (h - 2 * pad);
      return (
        <svg width={w} height={h} style={{ borderRadius: 6, background: alpha(theme.palette.background.default, 0.5), border: `1px solid ${theme.palette.divider}` }}>
          {/* Strike zone */}
          <rect x={sx(szLeft)} y={sy(szTopVal)} width={sx(szRight) - sx(szLeft)} height={sy(szBotVal) - sy(szTopVal)}
            fill="none" stroke={theme.palette.text.secondary} strokeWidth={1.2} strokeDasharray="3,2" />
          {/* 3x3 grid */}
          {[1/3, 2/3].map(f => (
            <React.Fragment key={f}>
              <line x1={sx(szLeft + f * (szRight - szLeft))} y1={sy(szTopVal)} x2={sx(szLeft + f * (szRight - szLeft))} y2={sy(szBotVal)}
                stroke={alpha(theme.palette.text.secondary, 0.12)} strokeWidth={0.5} />
              <line x1={sx(szLeft)} y1={sy(szBotVal + f * (szTopVal - szBotVal))} x2={sx(szRight)} y2={sy(szBotVal + f * (szTopVal - szBotVal))}
                stroke={alpha(theme.palette.text.secondary, 0.12)} strokeWidth={0.5} />
            </React.Fragment>
          ))}
          {/* Pitch dots */}
          {abPitches.map((p, i) => {
            if (p.pX == null || p.pZ == null) return null;
            const isLast = i === abPitches.length - 1;
            return (
              <g key={i}>
                <circle cx={sx(p.pX)} cy={sy(p.pZ)} r={isLast ? 7 : 5}
                  fill={getPitchDotColor(p.callCode)} stroke="white" strokeWidth={isLast ? 1.5 : 1} opacity={0.9} />
                <text x={sx(p.pX)} y={sy(p.pZ) + 3} textAnchor="middle"
                  fontSize={isLast ? 8 : 7} fill="white" fontWeight="bold">
                  {p.pitchNumber || i + 1}
                </text>
              </g>
            );
          })}
        </svg>
      );
    };

    // Mini field showing where ball landed
    const MiniHitField = () => {
      if (!ballInPlay) return null;
      const ev = currentAB.event?.toLowerCase() || '';
      let dotColor;
      if (ev.includes('home run')) dotColor = '#e74c3c';
      else if (ev.includes('triple')) dotColor = '#9b59b6';
      else if (ev.includes('double')) dotColor = '#3498db';
      else if (ev.includes('single')) dotColor = '#2ecc71';
      else dotColor = alpha(theme.palette.text.secondary, 0.5);
      return (
        <Box sx={{ textAlign: 'center' }}>
          <svg viewBox="0 0 250 250" width={isMobileView ? 100 : 120} style={{ display: 'block', margin: '0 auto' }}>
            <path d="M125,210 L20,105 A150,150 0 0,1 230,105 Z"
              fill={alpha('#4caf50', 0.06)} stroke={alpha('#4caf50', 0.2)} strokeWidth="0.8" />
            <polygon points="125,185 155,160 125,135 95,160"
              fill="none" stroke={alpha('#8d6e63', 0.25)} strokeWidth="0.6" />
            <circle cx={hitData.coordX} cy={hitData.coordY} r={6}
              fill={dotColor} stroke="white" strokeWidth={1.5} />
          </svg>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
            {currentAB.event}{hitData.exitVelocity ? ` • ${hitData.exitVelocity} mph` : ''}{hitData.distance ? ` • ${hitData.distance} ft` : ''}
          </Typography>
        </Box>
      );
    };

    return (
      <Card sx={{ mb: 2, borderRadius: 2, border: gameStatus.live ? `1px solid ${alpha(theme.palette.success.main, 0.3)}` : undefined }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          {/* Top row: pitcher vs batter + last play */}
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Avatar src={getPlayerHeadshotUrl(currentAB?.pitcherId || pitcher?.id)} sx={{ width: 28, height: 28 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', lineHeight: 1 }}>P</Typography>
                <Typography variant="body2" fontWeight="bold" noWrap sx={{ fontSize: '0.8rem' }}>
                  {currentAB?.pitcher || pitcher?.fullName || '—'}
                </Typography>
              </Box>
            </Stack>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">vs</Typography>
            <Stack direction="row-reverse" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Avatar src={getPlayerHeadshotUrl(currentAB?.batterId || batter?.id)} sx={{ width: 28, height: 28 }} />
              <Box sx={{ minWidth: 0, textAlign: 'right' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block', lineHeight: 1 }}>AB</Typography>
                <Typography variant="body2" fontWeight="bold" noWrap sx={{ fontSize: '0.8rem' }}>
                  {currentAB?.batter || batter?.fullName || '—'}
                </Typography>
              </Box>
            </Stack>
          </Stack>

          {/* Main row: diamond+count | strike zone | hit field */}
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={isMobileView ? 1 : 2}>
            {/* Diamond + BSO count */}
            {gameStatus.live && (
              <Stack alignItems="center" spacing={0.3} sx={{ flexShrink: 0 }}>
                <Diamond size={isMobileView ? 50 : 56} />
                <Stack spacing={0.15}>
                  <Stack direction="row" alignItems="center" spacing={0.4}>
                    <Typography variant="caption" sx={{ width: 8, fontSize: '0.6rem', color: 'text.secondary' }}>B</Typography>
                    <CountDots count={balls} max={4} activeColor="#4caf50" />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.4}>
                    <Typography variant="caption" sx={{ width: 8, fontSize: '0.6rem', color: 'text.secondary' }}>S</Typography>
                    <CountDots count={strikes} max={3} activeColor="#f44336" />
                  </Stack>
                  <Stack direction="row" alignItems="center" spacing={0.4}>
                    <Typography variant="caption" sx={{ width: 8, fontSize: '0.6rem', color: 'text.secondary' }}>O</Typography>
                    <CountDots count={outs} max={3} activeColor="#ff9800" />
                  </Stack>
                </Stack>
              </Stack>
            )}

            {/* Strike zone with pitches */}
            <MiniStrikeZone />

            {/* Hit location (only when ball in play) */}
            {ballInPlay && <MiniHitField />}
          </Stack>

          {/* Last play description */}
          {currentAB?.description && (
            <Typography variant="caption" color="text.secondary"
              sx={{ mt: 1, display: 'block', textAlign: 'center', lineHeight: 1.3, fontSize: '0.7rem' }}>
              {currentAB.description}
            </Typography>
          )}

          {/* Pitch legend */}
          {abPitches.length > 0 && (
            <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
              {[{ l: 'Ball', c: '#4caf50' }, { l: 'Called', c: '#f44336' }, { l: 'Swing', c: '#ff9800' }, { l: 'Foul', c: '#ffc107' }, { l: 'In Play', c: '#2196f3' }].map(i => (
                <Stack key={i.l} direction="row" alignItems="center" spacing={0.3}>
                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: i.c }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>{i.l}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>
    );
  };

  // ── Tab content ──
  // ════════════════════════════════════════════════════════════════════════════
  // MATCHUPS TAB — batter vs pitcher historical stats
  // ════════════════════════════════════════════════════════════════════════════
  const MatchupsTab = () => {
    if (matchupLoading) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1 }} />
        </Box>
      );
    }

    if (!matchupData || (!matchupData.awayVsHomePitcher && !matchupData.homeVsAwayPitcher)) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Person sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h6" color="text.secondary">No Matchup Data Available</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Batter vs pitcher history will appear here when data is available
          </Typography>
        </Box>
      );
    }

    const formatStat = (val) => {
      if (val == null || isNaN(val)) return '—';
      if (val < 1 && val >= 0) return val.toFixed(3).replace(/^0/, '');
      return String(val);
    };

    const renderMatchupTable = (data, teamAbbr, pitcherName, label) => {
      // Use career data if available, fall back to current season
      const batters = (data?.careerBatters?.length ? data.careerBatters : data?.batters) || [];
      if (!batters.length) return null;

      return (
        <Box sx={{ mb: 4 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Avatar src={getTeamLogoUrl(teamAbbr)} sx={{ width: 24, height: 24 }} />
            <Typography variant={isMobileView ? 'subtitle1' : 'h6'} fontWeight="bold" color="primary">
              {label}
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            vs{' '}
            <Typography
              component="span"
              variant="caption"
              sx={{ fontWeight: 'bold', cursor: 'pointer', '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' } }}
              onClick={() => {
                // Determine pitcher's team (the opposing team)
                const pitcherTeamAbbr = teamAbbr === awayTeam?.abbreviation
                  ? homeTeam?.abbreviation
                  : awayTeam?.abbreviation;
                setMatchupExplorer({ batterTeam: teamAbbr, batterName: null, pitcherTeam: pitcherTeamAbbr, pitcherName, perspective: 'pitcher' });
              }}
            >
              {pitcherName}
            </Typography>
            {' '}— {data?.careerBatters?.length ? 'Career' : 'This Season'}
          </Typography>
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                  <TableCell sx={{ fontWeight: 'bold', position: 'sticky', left: 0, bgcolor: alpha(theme.palette.background.paper, 0.95), zIndex: 2, minWidth: isMobileView ? 90 : 'auto' }}>Batter</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>PA</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>AB</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>H</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>HR</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>BB</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>SO</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>AVG</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>OBP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batters.map((b, i) => {
                  const s = b.stats || {};
                  const isHot = (s.avg || 0) >= 0.300;
                  return (
                    <TableRow key={i} sx={{
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                      ...(isHot && { bgcolor: alpha(theme.palette.success.main, 0.04) }),
                    }}>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          cursor: 'pointer',
                          position: 'sticky',
                          left: 0,
                          bgcolor: alpha(theme.palette.background.paper, 0.95),
                          zIndex: 2,
                          minWidth: isMobileView ? 90 : 'auto',
                          '&:hover': { color: theme.palette.primary.main, textDecoration: 'underline' },
                        }}
                        onClick={() => {
                          const pitcherTeamAbbr = teamAbbr === awayTeam?.abbreviation
                            ? homeTeam?.abbreviation
                            : awayTeam?.abbreviation;
                          setMatchupExplorer({ batterTeam: b.team || teamAbbr, batterName: b.name, pitcherTeam: pitcherTeamAbbr, pitcherName, perspective: 'batter' });
                        }}
                      >
                        {isMobileView ? getShortName(b.name) : b.name}
                        {b.seasonCount > 1 && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({b.seasonCount}yr)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">{s.plateAppearances || 0}</TableCell>
                      <TableCell align="center">{s.atBats || 0}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: (s.hits || 0) > 0 ? 'bold' : 'normal', color: (s.hits || 0) > 0 ? theme.palette.success.dark : 'inherit' }}>
                        {s.hits || 0}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: (s.homeRuns || 0) > 0 ? 'bold' : 'normal', color: (s.homeRuns || 0) > 0 ? theme.palette.error.main : 'inherit' }}>
                        {s.homeRuns || 0}
                      </TableCell>
                      <TableCell align="center">{s.walks || 0}</TableCell>
                      <TableCell align="center">{s.strikeouts || 0}</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: isHot ? theme.palette.success.dark : 'inherit' }}>
                        {formatStat(s.avg)}
                      </TableCell>
                      <TableCell align="center">{formatStat(s.obp)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      );
    };

    const homePitcherName = game?.probableHomePitcher
      || detailedStats?.pitchingStats?.home?.[0]?.name || 'Starter';
    const awayPitcherName = game?.probableAwayPitcher
      || detailedStats?.pitchingStats?.away?.[0]?.name || 'Starter';

    return (
      <Box>
        {renderMatchupTable(
          matchupData.awayVsHomePitcher,
          awayTeam?.abbreviation,
          homePitcherName,
          `${isMobileView ? awayTeam?.abbreviation : (awayTeam?.name || awayTeam?.abbreviation)} Lineup`
        )}
        {renderMatchupTable(
          matchupData.homeVsAwayPitcher,
          homeTeam?.abbreviation,
          awayPitcherName,
          `${isMobileView ? homeTeam?.abbreviation : (homeTeam?.name || homeTeam?.abbreviation)} Lineup`
        )}
      </Box>
    );
  };

  const renderTabContent = () => {
    // Pre-game: show probable pitchers + matchup data (no tabs)
    if (isPreGame) {
      return (
        <Box>
          {(game.probableHomePitcher || game.probableAwayPitcher) && (
            <Card sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                  Probable Pitchers
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-around">
                  <Stack alignItems="center" spacing={0.5}>
                    <Avatar src={getTeamLogoUrl(awayTeam?.abbreviation)} sx={{ width: 28, height: 28 }} />
                    <Typography variant="body2" fontWeight="bold">
                      {game.probableAwayPitcher || 'TBD'}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">vs</Typography>
                  <Stack alignItems="center" spacing={0.5}>
                    <Avatar src={getTeamLogoUrl(homeTeam?.abbreviation)} sx={{ width: 28, height: 28 }} />
                    <Typography variant="body2" fontWeight="bold">
                      {game.probableHomePitcher || 'TBD'}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}
          {MatchupsTab()}
        </Box>
      );
    }
    // Live / Final: 3-tab layout
    switch (currentTab) {
      case 0: return (
        <Box>
          {CurrentSituation()}
          {LineScoreTable()}
          {BattingStats()}
          {PitchingStats()}
          <Divider sx={{ my: 3 }} />
          <Typography variant="overline" color="text.secondary" fontWeight="bold"
            sx={{ mb: 1, display: 'block' }}>
            Batter vs Pitcher Matchups
          </Typography>
          {MatchupsTab()}
        </Box>
      );
      case 1: return PlayByPlay();
      case 2: return <WinProbabilityChart gameId={game.id} isLive={gameStatus.live} />;
      default: return null;
    }
  };

  // ── Matchup At-Bat Explorer Dialog ──
  const MatchupExplorerDialog = () => {
    if (!matchupExplorer) return null;
    const { batterTeam, batterName, pitcherTeam, pitcherName, perspective } = matchupExplorer;
    const isBatterView = perspective === 'batter';
    const team = isBatterView ? batterTeam : pitcherTeam;
    const playerName = isBatterView ? batterName : pitcherName;
    const vsPlayer = isBatterView ? pitcherName : batterName;
    const season = new Date().getFullYear().toString();
    // Title: "Batter vs Pitcher" or "Pitcher (all batters)" if no specific batter
    const title = batterName
      ? (isBatterView ? `${batterName} vs ${pitcherName}` : `${pitcherName} vs ${batterName}`)
      : `${pitcherName} — All At-Bats`;

    return (
      <Dialog
        open={!!matchupExplorer}
        onClose={() => setMatchupExplorer(null)}
        maxWidth="lg"
        fullWidth
        fullScreen={window.innerWidth < 600}
        PaperProps={{ sx: { maxHeight: { xs: '100vh', sm: '92vh' }, borderRadius: { sm: 2 } } }}
      >
        <DialogTitle sx={{ pb: 1, pr: 6 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <SportsBaseball sx={{ color: 'primary.main' }} />
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                  {title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pitch-by-pitch at-bat explorer — {season} season
                </Typography>
              </Box>
            </Stack>
            {/* Perspective toggle (only when both batter + pitcher are known) */}
            {batterName && (
              <Chip
                icon={<Person sx={{ fontSize: 16 }} />}
                label={isBatterView ? 'Batter View' : 'Pitcher View'}
                variant="outlined"
                size="small"
                onClick={() => setMatchupExplorer(prev => ({
                  ...prev,
                  perspective: prev.perspective === 'batter' ? 'pitcher' : 'batter',
                }))}
                sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) } }}
              />
            )}
          </Stack>
          <IconButton
            onClick={() => setMatchupExplorer(null)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'auto', pt: 1 }}>
          <AtBatExplorer
            key={`${team}-${playerName}-${perspective}-${vsPlayer}`}
            team={team}
            playerName={playerName}
            season={season}
            perspective={perspective}
            initialVsPlayer={vsPlayer || ''}
          />
        </DialogContent>
        <DialogActions>
          {batterName && (
            <Button
              size="small"
              onClick={() => navigateToPlayer(isBatterView ? batterName : pitcherName)}
            >
              View {isBatterView ? 'Batter' : 'Pitcher'} Profile
            </Button>
          )}
          <Button onClick={() => setMatchupExplorer(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ── Popups (rendered once, always available) ──
  const renderPopups = () => (
    <>
      {InningDetailDialog()}
      {PlayerPopover()}
      {MatchupExplorerDialog()}
    </>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════════════════════
  if (embedded) {
    return (
      <Box>
        <ScoreHeader />
        {!isPreGame && (
          <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ pb: '8px !important', pt: '8px !important' }}>
              <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}
                variant="scrollable" scrollButtons="auto">
                <Tab label="Box Score" />
                <Tab label={
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>Plays</span>
                    {gameStatus.live && currentTab === 1 && autoRefresh && (
                      <Chip label="LIVE" size="small" color="success"
                        sx={{ fontSize: '0.75rem', height: 16, animation: 'pulse 2s infinite',
                          '@keyframes pulse': { '0%,100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.05)' } } }} />
                    )}
                  </Stack>
                } />
                <Tab label="Win Prob" />
              </Tabs>
            </CardContent>
          </Card>
        )}
        {renderTabContent()}
        {renderPopups()}
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      fullScreen={window.innerWidth < 600}
      PaperProps={{ sx: { maxHeight: { xs: '100vh', sm: '90vh' }, overflow: 'hidden', borderRadius: { sm: 2 } } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end">
          <IconButton onClick={onClose} size="small"><Close /></IconButton>
        </Stack>
        <ScoreHeader />
        {!isPreGame && (
          <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)}
            variant="scrollable" scrollButtons="auto">
            <Tab label="Box Score" />
            <Tab label={
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <span>Plays</span>
                {gameStatus.live && currentTab === 1 && autoRefresh && (
                  <Chip label="LIVE" size="small" color="success"
                    sx={{ fontSize: '0.75rem', height: 16, animation: 'pulse 2s infinite',
                      '@keyframes pulse': { '0%,100%': { transform: 'scale(1)' }, '50%': { transform: 'scale(1.05)' } } }} />
                )}
              </Stack>
            } />
            <Tab label="Win Prob" />
          </Tabs>
        )}
      </DialogTitle>
      <DialogContent sx={{ overflow: 'auto' }}>
        {renderTabContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {onNavigateToFull && (game?.officialDate || game?.gameDate) && (
          <Button variant="contained" onClick={() => {
            const d = game.officialDate || (game.gameDate ? game.gameDate.split('T')[0] : new Date().toISOString().split('T')[0]);
            onNavigateToFull(game.id, d);
          }}>
            View Full Boxscore
          </Button>
        )}
      </DialogActions>
      {renderPopups()}
    </Dialog>
  );
};

export default Boxscore;
